"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import GetStartedButton from "./platform/GetStartedButtonLazy";
import ProfileMenu from "./platform/ProfileMenuLazy";
import ThreeDToggle from "./home/ThreeDToggle";
import { HEADER_LINKS, PRODUCT_LINKS, RESOURCE_LINKS } from "@/lib/site";

function ArrowRight({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={className} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function isActive(pathname: string, href: string) {
  if (href.startsWith("/#")) return false;
  return pathname === href || pathname.startsWith(`${href}/`);
}

const menuLabel = "px-3 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500";

const navLinkClass = (active: boolean) =>
  `group relative text-[13px] font-medium transition ${
    active ? "text-[#0b0f19] dark:text-white" : "text-slate-600 hover:text-[#0b0f19] dark:text-slate-300 dark:hover:text-white"
  }`;

function NavDot({ active }: { active: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`absolute -bottom-2 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-current transition ${
        active ? "opacity-100" : "opacity-0 group-hover:opacity-100"
      }`}
    />
  );
}

// "Resources" dropdown: opens on hover and keyboard focus (CSS), and on
// click/tap (state, for touch screens). The panel is always rendered, just
// hidden, so its links are in the server HTML for crawlers on every page.
function ResourcesMenu({ pathname }: { pathname: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = isActive(pathname, RESOURCE_LINKS.main.href) || isActive(pathname, RESOURCE_LINKS.builder.href);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="group/resources relative" onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls="resources-menu"
        onClick={() => setOpen((v) => !v)}
        className={`${navLinkClass(active)} inline-flex items-center gap-1`}
      >
        Resources
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-3.5 w-3.5 transition group-hover/resources:rotate-180" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
        </svg>
        <NavDot active={active} />
      </button>
      <div
        id="resources-menu"
        className={`absolute left-1/2 top-full z-10 w-[22rem] -translate-x-1/2 pt-5 transition duration-200 ${
          open ? "visible translate-y-0 opacity-100" : "invisible translate-y-1 opacity-0"
        } group-hover/resources:visible group-hover/resources:translate-y-0 group-hover/resources:opacity-100 group-focus-within/resources:visible group-focus-within/resources:translate-y-0 group-focus-within/resources:opacity-100`}
      >
        <div className="rounded-[1.5rem] bg-white p-2 shadow-[0_30px_70px_-20px_rgba(15,23,42,0.35)] ring-1 ring-slate-200/80 dark:bg-[#0f172a] dark:ring-white/10">
          <Link
            href={RESOURCE_LINKS.main.href}
            onClick={() => setOpen(false)}
            className="flex items-start gap-3 rounded-2xl bg-slate-50 p-3 transition hover:bg-slate-100 dark:bg-white/[0.04] dark:hover:bg-white/[0.08]"
          >
            <span aria-hidden="true" className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#0f766e] to-[#22c55e] text-white shadow-[0_8px_18px_-8px_rgba(16,185,129,0.8)]">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-5 w-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6" />
              </svg>
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-[#0b0f19] dark:text-white">{RESOURCE_LINKS.main.label}</span>
              <span className="mt-0.5 block text-xs leading-snug text-slate-500 dark:text-slate-400">{RESOURCE_LINKS.main.description}</span>
            </span>
          </Link>
          <Link
            href={RESOURCE_LINKS.builder.href}
            onClick={() => setOpen(false)}
            className="mt-1 flex items-start gap-3 rounded-2xl p-3 transition hover:bg-slate-50 dark:hover:bg-white/[0.06]"
          >
            <span aria-hidden="true" className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#4f46e5] to-[#a855f7] text-white shadow-[0_8px_18px_-8px_rgba(124,58,237,0.8)]">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-5 w-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" />
              </svg>
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-[#0b0f19] dark:text-white">{RESOURCE_LINKS.builder.label}</span>
              <span className="mt-0.5 block text-xs leading-snug text-slate-500 dark:text-slate-400">{RESOURCE_LINKS.builder.description}</span>
            </span>
          </Link>
          <p className={menuLabel}>Popular resume categories</p>
          <ul className="grid grid-cols-2 gap-0.5 px-1 pb-1">
            {RESOURCE_LINKS.categories.map((c) => (
              <li key={c.href}>
                <Link
                  href={c.href}
                  onClick={() => setOpen(false)}
                  className="block rounded-lg px-2 py-1.5 text-[13px] text-slate-600 transition hover:bg-slate-50 hover:text-[#0b0f19] dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white"
                >
                  {c.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// Site header: wordmark, centred section links, a "Start a Project" pill and
// a round menu button that opens the full menu (all links, products,
// sign-in and theme) at every screen size. Transparent over the top of the
// page so heroes show through, frosted once the page scrolls.
export default function Header() {
  const pathname = usePathname() ?? "/";
  // The home page has the 3D switch; on phones it takes the Start a Project
  // pill's place (the pill moves into the menu).
  const isHome = pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  // The menu is "open for" the page it was opened on, so any navigation --
  // a menu link, sign-in redirect, back button -- closes it without an
  // effect having to watch the pathname.
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  const menuOpen = menuOpenFor === pathname;
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpenFor(null);
        buttonRef.current?.focus();
      }
    };
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!panelRef.current?.contains(target) && !buttonRef.current?.contains(target)) setMenuOpenFor(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpenFor(null);
  const solid = scrolled || menuOpen;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        solid
          ? "bg-white/80 shadow-[0_1px_0_rgba(15,23,42,0.06)] backdrop-blur-md dark:bg-[#0b1220]/80 dark:shadow-[0_1px_0_rgba(255,255,255,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="relative mx-auto flex h-20 max-w-site items-center justify-between gap-6 px-4 sm:px-6">
        <Logo />

        <nav aria-label="Main" className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-10 lg:flex">
          {HEADER_LINKS.map((link) => {
            const active = isActive(pathname, link.href);
            const item = (
              <Link key={link.href} href={link.href} aria-current={active ? "page" : undefined} className={navLinkClass(active)}>
                {link.label}
                <NavDot active={active} />
              </Link>
            );
            // Resources sits just before About.
            return link.href === "/about" ? [<ResourcesMenu key="resources" pathname={pathname} />, item] : item;
          })}
        </nav>

        <div className="flex items-center gap-2.5 sm:gap-3">
          {isHome ? <ThreeDToggle /> : null}
          <Link
            href="/contact"
            className={`group inline-flex items-center gap-2 rounded-full bg-[#0b0f19] py-2.5 pl-5 pr-4 text-[13px] font-semibold text-white shadow-[0_10px_24px_-12px_rgba(15,23,42,0.7)] transition hover:-translate-y-0.5 ${isHome ? "max-sm:hidden" : "max-[379px]:hidden"} dark:bg-white dark:text-[#0b0f19]`}
          >
            Start a Project
            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
          </Link>
          <ProfileMenu />
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setMenuOpenFor(menuOpen ? null : pathname)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="site-menu"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#0b0f19] shadow-[0_6px_18px_-10px_rgba(15,23,42,0.5)] ring-1 ring-slate-200 transition hover:ring-slate-300 dark:bg-white/10 dark:text-white dark:ring-white/15 dark:hover:ring-white/30"
          >
            <span aria-hidden="true" className="relative block h-3 w-4">
              <span
                className={`absolute left-0 h-[1.5px] w-4 rounded-full bg-current transition-all duration-300 ${
                  menuOpen ? "top-[5.25px] rotate-45" : "top-[2px]"
                }`}
              />
              <span
                className={`absolute left-0 h-[1.5px] rounded-full bg-current transition-all duration-300 ${
                  menuOpen ? "top-[5.25px] w-4 -rotate-45" : "top-[8.5px] w-2.5"
                }`}
              />
            </span>
          </button>
        </div>

        {menuOpen ? (
          <div
            id="site-menu"
            ref={panelRef}
            className="absolute right-4 top-[calc(100%-0.25rem)] w-[min(24rem,calc(100vw-2rem))] rounded-[1.75rem] bg-white p-3 shadow-[0_30px_70px_-20px_rgba(15,23,42,0.35)] ring-1 ring-slate-200/80 sm:right-6 dark:bg-[#0f172a] dark:ring-white/10"
          >
            <nav aria-label="Site menu" className="rounded-[1.25rem] bg-slate-50 p-2 dark:bg-white/[0.04]">
              <p className={menuLabel}>Menu</p>
              <ul>
                {[
                  ...HEADER_LINKS.slice(0, -1),
                  { label: RESOURCE_LINKS.main.label, href: RESOURCE_LINKS.main.href },
                  { label: RESOURCE_LINKS.builder.label, href: RESOURCE_LINKS.builder.href },
                  ...HEADER_LINKS.slice(-1),
                  { label: "Contact", href: "/contact" },
                ].map((link) => {
                  const active = isActive(pathname, link.href);
                  return (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        onClick={closeMenu}
                        aria-current={active ? "page" : undefined}
                        className="group flex items-center justify-between rounded-xl px-3 py-2.5 text-lg font-semibold tracking-tight text-[#0b0f19] transition hover:bg-white dark:text-white dark:hover:bg-white/5"
                      >
                        <span className="flex items-center gap-2">
                          {link.label}
                          {active ? <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> : null}
                        </span>
                        <ArrowRight className="h-4 w-4 -translate-x-1 text-slate-400 opacity-0 transition group-hover:translate-x-0 group-hover:opacity-100" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="mt-2 px-1">
              <p className={menuLabel}>Our products</p>
              <ul className="grid grid-cols-2 gap-1">
                {PRODUCT_LINKS.map((product) => {
                  const inner = (
                    <>
                      <Image
                        src={product.icon}
                        alt=""
                        width={32}
                        height={32}
                        className="h-8 w-8 flex-shrink-0 rounded-lg object-cover"
                      />
                      <span className="min-w-0 leading-tight">
                        <span className="block text-sm font-semibold text-[#0b0f19] dark:text-white">{product.name}</span>
                        <span className="block truncate text-[11px] text-slate-500 dark:text-slate-400">{product.tagline}</span>
                      </span>
                    </>
                  );
                  const cls =
                    "flex items-center gap-2.5 rounded-xl p-2 transition hover:bg-slate-50 dark:hover:bg-white/5";
                  return (
                    // An odd last item spans both columns so the grid has no hole.
                    <li key={product.name} className="[&:last-child:nth-child(odd)]:col-span-2">
                      {product.external ? (
                        <a href={product.href} target="_blank" rel="noopener noreferrer" onClick={closeMenu} className={cls}>
                          {inner}
                        </a>
                      ) : (
                        <Link href={product.href} onClick={closeMenu} className={cls}>
                          {inner}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            <Link
              href="/contact"
              onClick={closeMenu}
              className={`mx-1 mt-3 flex items-center justify-center gap-2 rounded-full bg-[#0b0f19] py-3 text-sm font-semibold text-white ${isHome ? "sm:hidden" : "min-[380px]:hidden"} dark:bg-white dark:text-[#0b0f19]`}
            >
              Start a Project
              <ArrowRight className="h-4 w-4" />
            </Link>

            {/* Sign-in sits on the right so its right-aligned error tooltip
                opens inside the panel rather than off-screen. */}
            <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 px-2 pb-1 pt-3 dark:border-white/10">
              <ThemeToggle />
              <GetStartedButton />
            </div>
          </div>
        ) : null}
      </div>
    </header>
  );
}
